var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Exclude } from "class-transformer";
import { getCurrentContract } from "./contract.util";
import { TimeEntryType, WorkspaceFilterTypes, } from "./model";
import { findEmployeeById, findEmployeeByStaffNumber, findPositionById, findPositionInVenues, getAllContractsForRange, getContractForDate, getDepartment, getVenue, parseTimes, } from "./utils";
export class EmployeeFilters {
    constructor(init = {}) {
        this.filters = [{ type: "employeeStatus", value: "active" }];
        this.sortProperty = "lastName";
        this.sortDirection = "ascending";
        this.text = "";
        Object.assign(this, init);
    }
}
__decorate([
    Exclude(),
    __metadata("design:type", String)
], EmployeeFilters.prototype, "text", void 0);
export function matchesEmploymentType(employee, type, date) {
    const contract = !date
        ? getCurrentContract(employee.contracts)
        : typeof date === "string"
            ? getContractForDate(employee.contracts || [], date)
            : getAllContractsForRange(employee.contracts || [], date);
    if (Array.isArray(contract)) {
        return contract.some((c) => c.employmentType === type);
    }
    return contract?.employmentType === type;
}
export function resolveFilterPosition({ company, position, timeEntry, }) {
    const positionOrId = position ?? timeEntry?.positionId ?? timeEntry?.position ?? undefined;
    if (typeof positionOrId === "number") {
        return (findPositionInVenues(company.venues, positionOrId) ??
            findPositionInVenues(company.archivedVenues, positionOrId));
    }
    return positionOrId;
}
export function resolveFilterEmployee(context) {
    if (context.employee)
        return context.employee;
    if (context.timeEntry?.employeeId) {
        return findEmployeeById(context.company.employees, context.timeEntry.employeeId);
    }
    return undefined;
}
export function resolveFilterDateOrRange(context) {
    const { date, timeEntry } = context;
    if (date)
        return date;
    if (timeEntry?.date)
        return timeEntry.date;
    return undefined;
}
const skipTimeEntryFilters = ["employeeId", "employeeTag", "staffNumber"];
export function matchesFilter(filter, context) {
    const { company, venue, department } = context;
    const employee = resolveFilterEmployee(context);
    if (context.timeEntry && !employee && skipTimeEntryFilters.includes(filter.type)) {
        return false;
    }
    switch (filter.type) {
        case "venue":
        case "department":
        case "position": {
            const position = resolveFilterPosition(context);
            const positions = getPositionsByFilter(filter, company);
            return ((!venue || matchesVenueByWorkspaceFilter(filter, venue, positions)) &&
                (!department || matchesDepartmentByWorkspaceFilter(filter, department, company, positions)) &&
                (!position || positions?.some((p) => p.id === position.id)) &&
                (!employee || employee.positions.some((pos) => positions?.some((p) => p.id === pos.id))));
        }
        case "employmentType": {
            if (!employee) {
                return false;
            }
            const date = resolveFilterDateOrRange(context);
            return matchesEmploymentType(employee, filter.value, date);
        }
        case "employeeStatus":
            if (!employee) {
                return false;
            }
            return employee.status === filter.value;
        case "employeeId": {
            const position = resolveFilterPosition(context);
            const positions = getPositionsByFilter(filter, company);
            return ((!venue ||
                venue.departments.some((dep) => dep.positions.some((pos) => positions?.some((p) => p.id === pos.id)))) &&
                (!department || department.positions.some((pos) => positions?.some((p) => p.id === pos.id))) &&
                (!position || positions?.some((p) => p.id === position.id)) &&
                (!employee || employee.id === filter.value));
        }
        case "staffNumber": {
            const position = resolveFilterPosition(context);
            const positions = getPositionsByFilter(filter, company);
            return ((!venue ||
                venue.departments.some((dep) => dep.positions.some((pos) => positions?.some((p) => p.id === pos.id)))) &&
                (!department || department.positions.some((pos) => positions?.some((p) => p.id === pos.id))) &&
                (!position || positions?.some((p) => p.id === position.id)) &&
                (!employee || employee.staffNumber === filter.value));
        }
        case "employeeTag":
            if (!employee) {
                return false;
            }
            return employee.tags?.some((tag) => tag.id === filter.value);
        case "costCenter": {
            const constCenter = company.costCenters.find((cc) => cc.number === filter.value);
            if (!constCenter) {
                return false;
            }
            const date = resolveFilterDateOrRange(context);
            const position = resolveFilterPosition(context);
            return matchesFilters(constCenter.entities, {
                company,
                employee,
                date,
                venue,
                department,
                position,
            });
        }
    }
}
export function groupByFilterType(filters) {
    const filterTypes = {
        employeeStatus: [],
        employmentType: [],
        employeeTag: [],
        workspace: [],
        employee: [],
        costCenter: [],
    };
    for (const filter of filters) {
        switch (filter.type) {
            case "venue":
            case "department":
            case "position":
                filterTypes.workspace.push(filter);
                break;
            case "employeeId":
            case "staffNumber":
                filterTypes.employee.push(filter);
                break;
            case "employmentType":
                filterTypes.employmentType.push(filter);
                break;
            case "employeeStatus":
                filterTypes.employeeStatus.push(filter);
                break;
            case "employeeTag":
                filterTypes.employeeTag.push(filter);
                break;
            case "costCenter":
                filterTypes.costCenter.push(filter);
                break;
        }
    }
    return filterTypes;
}
export function extractWorkspaceFilters(filters) {
    return filters.filter((filter) => WorkspaceFilterTypes.includes(filter.type));
}
export function matchesFilters(filters, context) {
    const filterTypes = groupByFilterType(filters);
    return [...Object.values(filterTypes)].every((filters) => !filters.length || filters.some((filter) => matchesFilter(filter, context)));
}
export function matchesWorkspaceFilters(filters, context) {
    const workspaceFilters = groupByFilterType(filters).workspace;
    return !workspaceFilters.length || workspaceFilters.some((filter) => matchesFilter(filter, context));
}
export function matchesVenueWorkspace(filters, company, venue) {
    return matchesWorkspaceFilters(filters, {
        company,
        venue,
    });
}
export function matchesDepartmentWorkspace(filters, company, department) {
    return matchesWorkspaceFilters(filters, {
        company,
        department,
    });
}
export function matchesPositionWorkspace(filters, company, position) {
    return matchesWorkspaceFilters(filters, {
        company,
        position,
    });
}
export function matchesEmployeeWorkspace(filters, company, employee) {
    return matchesWorkspaceFilters(filters, {
        company,
        employee,
    });
}
export function getSpecificity(filters) {
    const types = new Set(filters.map((f) => f.type));
    let score = types.size;
    if (types.has("employeeId")) {
        score += 10;
    }
    return score;
}
export function serializeFilters(filters) {
    return btoa(JSON.stringify(filters)).replace(/=/g, "");
}
export function deserializeFilters(filters) {
    return JSON.parse(atob(filters));
}
/**
 * Optimizes sorting by properties that might have complicated calculations
 * by pre-calculating the sort properties and sorting by them.
 * The tradeoff is that we instantiate 3 new arrays and 2 new functions and NOT mutating the input array in place.
 */
export function sortBy(arr, fnBy, sortDirection) {
    // Pre-calculate sort properties once
    const sortProps = new Array(arr.length);
    const indices = new Array(arr.length);
    for (let i = 0; i < arr.length; i++) {
        sortProps[i] = fnBy(arr[i], i);
        indices[i] = i;
    }
    // sort indices by sort properties
    indices.sort((indexA, indexB) => {
        const aVal = sortProps[indexA];
        const bVal = sortProps[indexB];
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    });
    // create new array with sorted entries
    const ascending = sortDirection === "ascending";
    const result = new Array(arr.length);
    for (let i = 0; i < arr.length; i++) {
        result[i] = ascending ? arr[indices[i]] : arr[indices[arr.length - i - 1]];
    }
    return result;
}
// A venue or department that has no positions yet (e.g. freshly created) can never be reached through the
// positions a workspace filter resolves to, so venues and departments are also matched by their own identity.
function matchesVenueByWorkspaceFilter(filter, venue, positions) {
    return ((filter.type === "venue" && venue.id === filter.value) ||
        (filter.type === "department" && venue.departments.some((dep) => dep.id === filter.value)) ||
        venue.departments.some((dep) => dep.positions.some((pos) => positions.some((p) => p.id === pos.id))));
}
function matchesDepartmentByWorkspaceFilter(filter, department, company, positions) {
    return ((filter.type === "department" && department.id === filter.value) ||
        (filter.type === "venue" &&
            getDepartment([...company.venues, ...company.archivedVenues], department.id).venue?.id === filter.value) ||
        department.positions.some((pos) => positions.some((p) => p.id === pos.id)));
}
function getPositionsByFilter(filter, company) {
    switch (filter.type) {
        case "venue":
            return (getVenue([...company.venues, ...company.archivedVenues], filter.value)?.departments.flatMap((department) => department.positions) ?? []);
        case "department":
            return (getDepartment([...company.venues, ...company.archivedVenues], filter.value).department?.positions ?? []);
        case "position": {
            const position = findPositionById([...company.venues, ...company.archivedVenues], filter.value)?.position;
            return position ? [position] : [];
        }
        case "employeeId":
            return findEmployeeById(company.employees, filter.value)?.positions ?? [];
        case "staffNumber":
            return findEmployeeByStaffNumber(company.employees, filter.value)?.positions ?? [];
        case "employeeStatus":
        case "employmentType":
        case "employeeTag":
        case "costCenter":
            return [];
    }
}
export function matchesTimeFilter({ date, start, end, type, }, { from, to, fromRule, toRule }) {
    if (type !== TimeEntryType.Work) {
        return true;
    }
    if (typeof start === "string") {
        start = new Date(start);
    }
    if (typeof end === "string") {
        end = new Date(end);
    }
    const [filterStart, filterEnd] = parseTimes(date, from, to);
    let startOk = false;
    let endOk = false;
    switch (fromRule) {
        case "equal":
            startOk = !filterStart || (!!start && filterStart.getTime() === start.getTime());
            break;
        case "open":
            // Does it end before our filter starts (within or later)
            startOk = !filterStart || !end || end > filterStart;
            break;
        default:
            startOk = !filterStart || ((!start || start >= filterStart) && (!end || end > filterStart));
            break;
    }
    switch (toRule) {
        case "equal":
            endOk = !filterEnd || (!!end && filterEnd.getTime() === end.getTime());
            break;
        case "open":
            // does it start before our filter ends (within or earlier)
            endOk = !filterEnd || !start || start < filterEnd;
            break;
        default:
            endOk = !filterEnd || ((!end || end <= filterEnd) && (!start || start < filterEnd));
            break;
    }
    return startOk && endOk;
}
//# sourceMappingURL=filters.js.map